// errorMessage.js — turns an API failure into something worth showing a person.
//
// TARGET PATH: src/shared/api/errorMessage.js
//
// The API reports a rejected request like this:
//
//   {
//     "error":   "ValidationError",
//     "message": "Invalid request format",
//     "details": { "reason": "must not be blank",
//                  "rcaSummary": "RCA summary is required" }
//   }
//
// `message` is the same sentence for every validation failure in the system, so
// showing it alone tells the reader nothing — "Invalid request format" is how
// TSB-4219 managed to stay a mystery. The actionable part is `details`, keyed by
// field.
//
// BRS US 7.1 asks for exactly this: "Error messages MUST be clear and
// actionable."
//
// A 403 needs no special case here — GlobalExceptionHandler answers it with
// this same envelope (confirmed against source, not assumed), `details: null`,
// and a `message` that's already a complete sentence a user can read
// ("You do not have permission to access this resource"), so it falls
// through to the plain `data.message` return below like any other error that
// carries no per-field details.

/**
 * Bean Validation's own wording is a fragment — "must not be blank" — which
 * needs its field name to mean anything. Messages written by hand already name
 * their subject ("RCA summary is required") and would read as a stutter if the
 * field were prepended.
 */
const describe = ([field, message]) =>
  /^must\b/i.test(message) ? `${field} ${message}` : message;

/**
 * @param error    the rejected axios error
 * @param fallback what to say when the response carries nothing usable
 */
export const apiErrorMessage = (error, fallback) => {
  // A timeout has no response to read, and the caller's fallback ("Failed to
  // save...") would describe it as a rejection when nothing was ever answered.
  // Worth saying plainly, because the action is different: a rejected request
  // needs the input changed, a timed-out one just needs trying again.
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return 'The request timed out. Please try again.';
  }

  const data = error?.response?.data;
  if (!data) return fallback;
  // Some gateway failures answer with plain text rather than the JSON envelope.
  if (typeof data === 'string') return data.trim() || fallback;

  const details = data.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const described = Object.entries(details)
      .filter(([, message]) => typeof message === 'string' && message.trim())
      .map(describe);

    if (described.length) return described.join(' · ');
  }

  return data.message || fallback;
};

export default apiErrorMessage;
